// src/pages/student-portal/dashboard/StudentDashboard.js
// Student Portal dashboard overview — ported from Next.js/Tailwind StudentDashboardContent

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Skeleton,
    Avatar,
    Chip,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatCard = ({ icon: Icon, title, value, color, subtitle, onClick }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick
                ? { borderColor: color, boxShadow: `0 4px 20px ${color}20`, transform: 'translateY(-2px)' }
                : {},
        }}
        onClick={onClick}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const CARD_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

const ActiveCourseCard = ({ course, index, onNavigate }) => {
    const progress = course.progress ?? course.progress_percentage ?? course.completion_percentage ?? 0;
    const courseId = course.course_identity || course.course?.course_identity || course.slug || course.course_id;

    return (
        <Card
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 30px rgba(139, 92, 246, 0.12)',
                    borderColor: ACCENT,
                },
            }}
            onClick={() => onNavigate(`/student-portal/courses/${courseId}`)}
        >
            {/* Thumbnail / gradient banner */}
            <Box
                sx={{
                    height: 90,
                    background: (course.thumbnail_url || course.thumbnail || course.course?.thumbnail_url)
                        ? `url(${course.thumbnail_url || course.thumbnail || course.course?.thumbnail_url}) center/cover no-repeat`
                        : CARD_GRADIENTS[index % CARD_GRADIENTS.length],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}
            >
                {!(course.thumbnail_url || course.thumbnail || course.course?.thumbnail_url) && (
                    <AutoStoriesIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.6)' }} />
                )}
                {/* Progress chip overlay */}
                <Chip
                    label={`${Math.round(progress)}%`}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontFamily,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: progress >= 100 ? '#10b981' : ACCENT,
                        height: 24,
                    }}
                />
            </Box>

            <CardContent sx={{ p: 2 }}>
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.4,
                        minHeight: '2.4em',
                    }}
                >
                    {course.title || course.course_title || course.course?.title || 'Untitled Course'}
                </Typography>

                {/* Progress bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontFamily, color: '#64748b' }}>
                        Progress
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily, fontWeight: 700, color: ACCENT }}>
                        {Math.round(progress)}%
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(progress, 100)}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#e2e8f0',
                        mb: 1.5,
                        '& .MuiLinearProgress-bar': {
                            bgcolor: progress >= 100 ? '#10b981' : ACCENT,
                            borderRadius: 3,
                        },
                    }}
                />

                {/* Continue Learning link */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PlayArrowIcon sx={{ fontSize: 16, color: ACCENT }} />
                    <Typography
                        sx={{
                            fontFamily,
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            color: ACCENT,
                        }}
                    >
                        Continue Learning
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const BADGE_COLORS = ['#8b5cf6', '#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#ec4899'];

const BadgeItem = ({ badge, index }) => {
    const badgeColor = badge.color || BADGE_COLORS[index % BADGE_COLORS.length];
    const badgeIcon = badge.icon || null;
    const earnedDate = badge.earned_at || badge.date || badge.created_at;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 0.5,
                borderBottom: '1px solid #f1f5f9',
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Avatar
                sx={{
                    width: 42,
                    height: 42,
                    bgcolor: `${badgeColor}18`,
                    color: badgeColor,
                    fontSize: '1.1rem',
                }}
            >
                {badgeIcon ? (
                    <span role="img" aria-label="badge">{badgeIcon}</span>
                ) : (
                    <EmojiEventsIcon sx={{ fontSize: 22 }} />
                )}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {badge.name || badge.title || 'Badge'}
                </Typography>
                {badge.description && (
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily,
                            color: '#94a3b8',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {badge.description}
                    </Typography>
                )}
            </Box>
            {earnedDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        {new Date(earnedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                        })}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

const CertificateItem = ({ certificate }) => {
    const issuedDate = certificate.issued_at || certificate.date || certificate.created_at;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 0.5,
                borderBottom: '1px solid #f1f5f9',
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Avatar
                sx={{
                    width: 42,
                    height: 42,
                    bgcolor: '#dbeafe',
                    color: '#3b82f6',
                }}
            >
                <MilitaryTechIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {certificate.course_title || certificate.title || 'Certificate'}
                </Typography>
                {issuedDate && (
                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                        Issued{' '}
                        {new Date(issuedDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                    </Typography>
                )}
            </Box>
            <Chip
                icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
                label="Verified"
                size="small"
                sx={{
                    fontFamily,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: '#ecfdf5',
                    color: '#10b981',
                    borderColor: '#a7f3d0',
                    border: '1px solid',
                    height: 26,
                    '& .MuiChip-icon': { color: '#10b981' },
                }}
            />
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Skeleton loaders
// ---------------------------------------------------------------------------

const StatCardSkeleton = () => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const CourseCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
        <Skeleton variant="rectangular" height={90} />
        <CardContent sx={{ p: 2 }}>
            <Skeleton variant="text" sx={{ fontSize: '0.9rem', mb: 0.5 }} />
            <Skeleton variant="text" width="70%" sx={{ fontSize: '0.8rem', mb: 1 }} />
            <Skeleton variant="rounded" height={6} sx={{ borderRadius: 3, mb: 1.5 }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem' }} />
        </CardContent>
    </Card>
);

const ListItemSkeleton = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
        <Skeleton variant="circular" width={42} height={42} />
        <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
            <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------
// Helper: compute level label from total points
// ---------------------------------------------------------------------------

const getLevelInfo = (points) => {
    const p = points || 0;
    if (p >= 5000) return { label: 'Master', color: '#f59e0b' };
    if (p >= 2000) return { label: 'Expert', color: '#8b5cf6' };
    if (p >= 1000) return { label: 'Advanced', color: '#3b82f6' };
    if (p >= 500) return { label: 'Intermediate', color: '#10b981' };
    if (p >= 100) return { label: 'Beginner', color: '#6366f1' };
    return { label: 'Newcomer', color: '#94a3b8' };
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function StudentDashboard() {
    const navigate = useNavigate();

    const [dashboardData, setDashboardData] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const firstName = user?.profile?.fname || user?.first_name || user?.name || 'Student';

    // Fetch dashboard data, with fallback to my-courses
    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // Try the dedicated dashboard endpoint first
            const response = await axiosInstance.get('/api/lms/portal/dashboard');
            const data = response.data?.data || response.data || {};

            setDashboardData({
                stats: data.stats || {},
                active_courses: data.active_courses || [],
                recent_badges: data.recent_badges || [],
                certificates: data.certificates || [],
                total_points: data.stats?.total_points ?? 0,
                level: data.student?.current_level || data.stats?.current_level || null,
            });
            setCourses(data.active_courses || []);
        } catch (dashErr) {
            console.warn('Dashboard endpoint unavailable, falling back to my-courses:', dashErr?.message);
            try {
                // Fallback: fetch courses list
                const fallbackRes = await axiosInstance.get('/api/lms/portal/my-courses');
                const fallbackData = fallbackRes.data;
                const allCourses = fallbackData.courses || fallbackData.data || fallbackData.results || [];

                const activeCourses = allCourses.filter(
                    (c) => c.status !== 'completed' && (c.progress == null || c.progress < 100)
                );
                const completedCourses = allCourses.filter(
                    (c) => c.status === 'completed' || (c.progress != null && c.progress >= 100)
                );

                setDashboardData({
                    stats: {
                        active_courses: activeCourses.length,
                        completed_courses: completedCourses.length,
                        certificates_earned: completedCourses.length,
                        total_points: 0,
                    },
                    active_courses: activeCourses,
                    recent_badges: [],
                    certificates: [],
                    total_points: 0,
                    level: null,
                });
                setCourses(activeCourses);
            } catch (fallbackErr) {
                console.error('Failed to fetch dashboard data:', fallbackErr);
                setError(fallbackErr.response?.data?.message || 'Failed to load dashboard.');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    // Derived values
    const stats = dashboardData?.stats || {};
    const activeCourses = (dashboardData?.active_courses || courses).slice(0, 4);
    const recentBadges = (dashboardData?.recent_badges || []).slice(0, 5);
    const recentCertificates = (dashboardData?.certificates || []).slice(0, 5);
    const totalPoints = dashboardData?.total_points ?? stats.total_points ?? 0;
    const levelInfo = dashboardData?.level
        ? { label: dashboardData.level, color: ACCENT }
        : getLevelInfo(totalPoints);

    const statActiveCourses = stats.active_courses ?? activeCourses.length;
    const statCompletedCourses = stats.completed_courses ?? 0;
    const statCertificates = stats.certificates_earned ?? recentCertificates.length;
    const statTotalPoints = stats.total_points ?? totalPoints;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ---------------------------------------------------------------- */}
            {/* Welcome Header with Gradient                                     */}
            {/* ---------------------------------------------------------------- */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.06)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -40,
                        right: 80,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.04)',
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2,
                    }}
                >
                    {/* Left: Welcome text */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Avatar
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                }}
                            >
                                {user?.profile?.avatar ? (
                                    <Box
                                        component="img"
                                        src={user.profile.avatar}
                                        alt={firstName}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <PersonIcon sx={{ fontSize: 26 }} />
                                )}
                            </Avatar>
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 800,
                                        color: 'white',
                                        fontSize: { xs: '1.4rem', md: '1.85rem' },
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Welcome back, {firstName}!
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '0.9rem',
                                        mt: 0.25,
                                    }}
                                >
                                    Here's your learning overview
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right: Points & Level */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        {/* Total Points */}
                        <Box
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: '14px',
                                px: 2.5,
                                py: 1.5,
                                textAlign: 'center',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.15)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                                <StarIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 800,
                                        color: '#fff',
                                        fontSize: '1.35rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {loading ? '--' : totalPoints.toLocaleString()}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily,
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    mt: 0.25,
                                }}
                            >
                                Total Points
                            </Typography>
                        </Box>

                        {/* Level */}
                        <Box
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                borderRadius: '14px',
                                px: 2.5,
                                py: 1.5,
                                textAlign: 'center',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.15)',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                                <TrendingUpIcon sx={{ fontSize: 18, color: '#a5f3fc' }} />
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 800,
                                        color: '#fff',
                                        fontSize: '1.1rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    {loading ? '--' : levelInfo.label}
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily,
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    mt: 0.25,
                                }}
                            >
                                Level
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* ------------------------------------------------------------ */}
                {/* Error Alert                                                  */}
                {/* ------------------------------------------------------------ */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchDashboard}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ------------------------------------------------------------ */}
                {/* Stats Grid (4 cards)                                         */}
                {/* ------------------------------------------------------------ */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={AutoStoriesIcon}
                                title="Active Courses"
                                value={statActiveCourses}
                                color={ACCENT}
                                subtitle="Currently enrolled"
                                onClick={() => navigate('/student-portal/my-courses')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={CheckCircleIcon}
                                title="Completed Courses"
                                value={statCompletedCourses}
                                color="#10b981"
                                subtitle="Finished & certified"
                                onClick={() => navigate('/student-portal/my-courses')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={MilitaryTechIcon}
                                title="Certificates Earned"
                                value={statCertificates}
                                color="#3b82f6"
                                subtitle="Verified credentials"
                                onClick={() => navigate('/student-portal/certificates')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? (
                            <StatCardSkeleton />
                        ) : (
                            <StatCard
                                icon={StarIcon}
                                title="Total Points"
                                value={statTotalPoints.toLocaleString()}
                                color="#f59e0b"
                                subtitle={levelInfo.label}
                                onClick={() => navigate('/student-portal/leaderboard')}
                            />
                        )}
                    </Grid>
                </Grid>

                {/* ------------------------------------------------------------ */}
                {/* Continue Learning (Active Courses)                           */}
                {/* ------------------------------------------------------------ */}
                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlayArrowIcon sx={{ color: ACCENT, fontSize: 22 }} />
                            <Typography
                                variant="h6"
                                sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}
                            >
                                Continue Learning
                            </Typography>
                        </Box>
                        <Button
                            onClick={() => navigate('/student-portal/my-courses')}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                color: ACCENT,
                                '&:hover': { bgcolor: `${ACCENT}08` },
                            }}
                        >
                            View All
                        </Button>
                    </Box>

                    {loading ? (
                        <Grid container spacing={2.5}>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <CourseCardSkeleton />
                                </Grid>
                            ))}
                        </Grid>
                    ) : activeCourses.length === 0 ? (
                        <Paper
                            sx={{
                                py: 5,
                                px: 3,
                                textAlign: 'center',
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                            }}
                        >
                            <MenuBookIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
                                No active courses
                            </Typography>
                            <Typography
                                sx={{ fontFamily, color: '#64748b', mb: 2, fontSize: '0.9rem' }}
                            >
                                Browse available courses and start your learning journey!
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/student-portal/courses')}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    bgcolor: ACCENT,
                                    borderRadius: '12px',
                                    px: 3,
                                    boxShadow: 'none',
                                    '&:hover': { bgcolor: '#7c3aed', boxShadow: 'none' },
                                }}
                            >
                                Browse Courses
                            </Button>
                        </Paper>
                    ) : (
                        <Grid container spacing={2.5}>
                            {activeCourses.map((course, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={3}
                                    key={course.id || course._id || course.course_id || index}
                                >
                                    <ActiveCourseCard
                                        course={course}
                                        index={index}
                                        onNavigate={navigate}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                {/* ------------------------------------------------------------ */}
                {/* Bottom Grid: Recent Badges + Recent Certificates             */}
                {/* ------------------------------------------------------------ */}
                <Grid container spacing={3}>
                    {/* Recent Badges */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                                height: '100%',
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                {/* Section header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 2.5,
                                        pt: 2.5,
                                        pb: 1.5,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EmojiEventsIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                                        <Typography
                                            sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                color: '#1e293b',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            Recent Badges
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/student-portal/achievements')}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            color: ACCENT,
                                            fontSize: '0.78rem',
                                            '&:hover': { bgcolor: `${ACCENT}08` },
                                        }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                {/* Badges list */}
                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <ListItemSkeleton key={i} />
                                        ))
                                    ) : recentBadges.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <EmojiEventsIcon
                                                sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    color: '#94a3b8',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                No badges earned yet
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    color: '#cbd5e1',
                                                    fontSize: '0.78rem',
                                                    mt: 0.5,
                                                }}
                                            >
                                                Complete courses and activities to earn badges
                                            </Typography>
                                        </Box>
                                    ) : (
                                        recentBadges.map((badge, index) => (
                                            <BadgeItem
                                                key={badge.id || badge._id || index}
                                                badge={badge}
                                                index={index}
                                            />
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Certificates */}
                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                boxShadow: 'none',
                                height: '100%',
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                {/* Section header */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 2.5,
                                        pt: 2.5,
                                        pb: 1.5,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <MilitaryTechIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography
                                            sx={{
                                                fontFamily,
                                                fontWeight: 700,
                                                color: '#1e293b',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            Recent Certificates
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/student-portal/certificates')}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            color: ACCENT,
                                            fontSize: '0.78rem',
                                            '&:hover': { bgcolor: `${ACCENT}08` },
                                        }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                {/* Certificates list */}
                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <ListItemSkeleton key={i} />
                                        ))
                                    ) : recentCertificates.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <MilitaryTechIcon
                                                sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    color: '#94a3b8',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                No certificates yet
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    color: '#cbd5e1',
                                                    fontSize: '0.78rem',
                                                    mt: 0.5,
                                                }}
                                            >
                                                Complete courses to earn verified certificates
                                            </Typography>
                                        </Box>
                                    ) : (
                                        recentCertificates.map((cert, index) => (
                                            <CertificateItem
                                                key={cert.id || cert._id || index}
                                                certificate={cert}
                                            />
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
